import React, { useState, useEffect } from 'react';
import { FaFileExcel, FaMoneyBillWave, FaBuilding, FaChartLine } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const exportToCSV = (rows, filename) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

function FinancePage() {
  const { admin, getAdminToken } = useAuth();
  const [revenueData, setRevenueData] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    paidCount: 0,
    pendingCount: 0,
    overdueCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('this_month');

  useEffect(() => {
    fetchFinanceData();
  }, [selectedPeriod]);

  const fetchFinanceData = async () => {
    try {
      // Revenue logs fetch
      const { data: res, error } = await supabase.rpc('admin_get_revenue_logs_full', { p_admin_token: getAdminToken() });
      if (error) throw error;
      if (!res?.success) throw new Error(res?.error);
      const revenueLogs = res.rows.map(r => ({ ...r, businesses: { name: r.business_name } }));

      // Period filter
      let filteredLogs = revenueLogs;
      const now = new Date();
      
      if (selectedPeriod === 'this_month') {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        filteredLogs = revenueLogs.filter(
          (log) => new Date(log.created_at) >= startOfMonth
        );
      } else if (selectedPeriod === 'last_month') {
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        filteredLogs = revenueLogs.filter(
          (log) =>
            new Date(log.created_at) >= startOfLastMonth &&
            new Date(log.created_at) <= endOfLastMonth
        );
      }

      setRevenueData(filteredLogs);

      // Calculate stats
      const totalRevenue = filteredLogs.reduce(
        (sum, log) => sum + parseFloat(log.amount || 0),
        0
      );
      const paidCount = filteredLogs.filter((log) => log.status === 'paid').length;
      const pendingCount = filteredLogs.filter((log) => log.status === 'pending').length;
      const overdueCount = filteredLogs.filter((log) => log.status === 'overdue').length;

      setStats({
        totalRevenue,
        paidCount,
        pendingCount,
        overdueCount,
      });
    } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (logId) => {
    if (!window.confirm('Bu ödənişi "Paid" kimi işarələyirsiniz?')) return;

    try {
      const { data, error } = await supabase.rpc('admin_mark_revenue_paid', { p_admin_token: getAdminToken(), p_log_id: logId });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);

      alert('Ödəniş uğurla yeniləndi!');
      fetchFinanceData();
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Xəta baş verdi!');
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'paid':
        return { backgroundColor: '#E8F5E9', color: '#2E7D32', text: '✅ Ödənilib' };
      case 'pending':
        return { backgroundColor: '#FFF3E0', color: '#F57C00', text: '🟡 Gözləyir' };
      case 'overdue':
        return { backgroundColor: '#FFEBEE', color: '#C62828', text: '🔴 Gecikmiş' };
      default:
        return { backgroundColor: '#F5F5F5', color: '#666', text: 'N/A' };
    }
  };

  // Chart data
  const chartData = revenueData
    .reduce((acc, log) => {
      const businessName = log.businesses?.name || 'Unknown';
      const existing = acc.find((item) => item.name === businessName);
      if (existing) {
        existing.revenue += parseFloat(log.amount || 0);
      } else {
        acc.push({ name: businessName, revenue: parseFloat(log.amount || 0) });
      }
      return acc;
    }, [])
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>💰 Maliyyə & Gəlir İdarəetməsi</h1>
          <p style={styles.subtitle}>Revenue tracking və subscription idarəetməsi</p>
        </div>
        <div style={styles.headerActions}>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={styles.periodSelect}
          >
            <option value="all">Bütün Tarix</option>
            <option value="this_month">Bu Ay</option>
            <option value="last_month">Keçən Ay</option>
          </select>
          <button style={styles.btnExport} onClick={() => exportToCSV(revenueData.map(log => ({
            business: log.businesses?.name || 'N/A',
            amount: log.amount,
            type: log.type || 'subscription',
            period_start: log.period_start,
            period_end: log.period_end,
            status: log.status,
            created_at: log.created_at,
          })), 'finance_report.csv')}>
            <FaFileExcel /> Export Report
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #06D6A0' }}>
          <div style={styles.statIcon}>
            <FaMoneyBillWave size={32} color="#06D6A0" />
          </div>
          <div>
            <p style={styles.statLabel}>Ümumi Gəlir</p>
            <h2 style={styles.statValue}>{stats.totalRevenue.toFixed(2)} ₼</h2>
            <p style={styles.statSubtext}>Seçilmiş period</p>
          </div>
        </div>

        <div style={{ ...styles.statCard, borderLeft: '4px solid #2E7D32' }}>
          <div style={styles.statIcon}>
            <FaBuilding size={32} color="#2E7D32" />
          </div>
          <div>
            <p style={styles.statLabel}>Ödənilib</p>
            <h2 style={styles.statValue}>{stats.paidCount}</h2>
            <p style={styles.statSubtext}>Business sayı</p>
          </div>
        </div>

        <div style={{ ...styles.statCard, borderLeft: '4px solid #F57C00' }}>
          <div style={styles.statIcon}>
            <FaChartLine size={32} color="#F57C00" />
          </div>
          <div>
            <p style={styles.statLabel}>Gözləyir</p>
            <h2 style={styles.statValue}>{stats.pendingCount}</h2>
            <p style={styles.statSubtext}>Pending payments</p>
          </div>
        </div>

        <div style={{ ...styles.statCard, borderLeft: '4px solid #C62828' }}>
          <div style={styles.statIcon}>
            <FaMoneyBillWave size={32} color="#C62828" />
          </div>
          <div>
            <p style={styles.statLabel}>Gecikmiş</p>
            <h2 style={styles.statValue}>{stats.overdueCount}</h2>
            <p style={styles.statSubtext}>Overdue payments</p>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      {chartData.length > 0 && (
        <div style={styles.chartContainer}>
          <h3 style={styles.chartTitle}>📊 Business-ə görə Gəlir</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#999" />
              <YAxis tick={{ fontSize: 12 }} stroke="#999" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '10px',
                }}
                formatter={(value) => `${value.toFixed(2)} ₼`}
              />
              <Bar dataKey="revenue" fill="#06D6A0" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Payment History Table */}
      <div style={styles.tableSection}>
        <h3 style={styles.sectionTitle}>📋 Ödəniş Tarixi</h3>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>Business</th>
                <th style={styles.th}>Məbləğ</th>
                <th style={styles.th}>Tip</th>
                <th style={styles.th}>Period</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Tarix</th>
                <th style={styles.th}>Əməliyyat</th>
              </tr>
            </thead>
            <tbody>
              {revenueData.map((log) => {
                const statusStyle = getStatusStyle(log.status);
                return (
                  <tr key={log.id} style={styles.row}>
                    <td style={styles.td}>
                      <strong>{log.businesses?.name || 'N/A'}</strong>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.amount}>{parseFloat(log.amount).toFixed(2)} ₼</span>
                    </td>
                    <td style={styles.td}>{log.type || 'subscription'}</td>
                    <td style={styles.td}>
                      {log.period_start && log.period_end
                        ? `${new Date(log.period_start).toLocaleDateString('az-AZ')} - ${new Date(
                            log.period_end
                          ).toLocaleDateString('az-AZ')}`
                        : 'N/A'}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          backgroundColor: statusStyle.backgroundColor,
                          color: statusStyle.color,
                        }}
                      >
                        {statusStyle.text}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {new Date(log.created_at).toLocaleDateString('az-AZ')}
                    </td>
                    <td style={styles.td}>
                      {log.status !== 'paid' && (
                        <button
                          onClick={() => handleMarkAsPaid(log.id)}
                          style={styles.btnPaid}
                        >
                          Ödənildi
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {revenueData.length === 0 && (
            <div style={styles.noData}>
              <p>Heç bir ödəniş qeydi yoxdur</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1600px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  headerActions: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
  },
  periodSelect: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  btnExport: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
  },
  statIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    backgroundColor: '#F8F9FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statLabel: {
    fontSize: '13px',
    color: '#999',
    fontWeight: '500',
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  statSubtext: {
    fontSize: '12px',
    color: '#666',
  },
  chartContainer: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    marginBottom: '30px',
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '20px',
  },
  tableSection: {
    marginBottom: '30px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '20px',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '1000px',
  },
  headerRow: {
    backgroundColor: '#F8F9FA',
  },
  th: {
    padding: '15px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#666',
    textTransform: 'uppercase',
    borderBottom: '2px solid #E0E0E0',
  },
  row: {
    borderBottom: '1px solid #F0F0F0',
  },
  td: {
    padding: '15px',
    fontSize: '14px',
    color: '#1A1A2E',
  },
  amount: {
    fontWeight: 'bold',
    color: '#06D6A0',
    fontSize: '15px',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  btnPaid: {
    padding: '8px 15px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  noData: {
    padding: '40px',
    textAlign: 'center',
    color: '#999',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default FinancePage;